'use client';

import { useEffect, useRef } from 'react';
import { useAuthStore, refreshAccessToken } from '@/hooks/use-auth';
import { isAuthorFrame } from '@/lib/tours/author-frame';

/**
 * Access tokens live 15 minutes. Refresh at ~12 so the token is never actually
 * allowed to expire — reactive refresh-on-401 is what puts several requests
 * into a rotation race at the same instant.
 */
const REFRESH_AT_MS = 12 * 60 * 1000;

/**
 * Per-tab jitter, drawn once at mount. Without it every tab of every user
 * refreshes on the same boundary, which is the lockstep behaviour that made the
 * race reproducible in the first place.
 */
const MAX_JITTER_MS = 90 * 1000;

/**
 * Poll interval. Browsers clamp timers in background tabs to >= 1 minute, so
 * this figure is nominal — the visibility/focus listeners are what actually
 * cover a machine waking from sleep.
 */
const TICK_MS = 30 * 1000;

/** Never attempt a scheduled refresh more often than this, whatever the state. */
const MIN_ATTEMPT_SPACING_MS = 60 * 1000;

function proactiveRefreshEnabled(): boolean {
  // Kill switch (D26). Auth is the highest-blast-radius surface in the
  // platform; shipping it with no production lever is not acceptable.
  return process.env.NEXT_PUBLIC_PROACTIVE_REFRESH_ENABLED !== 'false';
}

/**
 * Keep the session alive ahead of expiry instead of discovering it has died.
 *
 * Deliberately compares wall-clock against `tokenIssuedAt` on a short interval
 * rather than scheduling one long `setTimeout`: a suspended laptop does not fire
 * timers, and "closed the lid mid-edit" is the reported scenario.
 */
export function useProactiveRefresh(): void {
  // Fixed for the lifetime of this tab.
  const jitterRef = useRef<number>(Math.floor(Math.random() * MAX_JITTER_MS));
  const inFlightRef = useRef(false);
  const lastAttemptRef = useRef(0);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!proactiveRefreshEnabled()) return;
    // The author iframe must never POST the shared admin refresh cookie — it
    // would return an ADMIN token and silently revert the iframe to admin data.
    if (isAuthorFrame()) return;

    const threshold = REFRESH_AT_MS + jitterRef.current;

    const maybeRefresh = () => {
      const { isAuthenticated, tokenIssuedAt, sessionExpired, sessionKind } =
        useAuthStore.getState();

      if (!isAuthenticated || !tokenIssuedAt) return;
      if (sessionExpired) return;
      if (sessionKind === 'unattended') return;
      if (inFlightRef.current) return;
      if (Date.now() - tokenIssuedAt < threshold) return;
      // Floor between attempts. `tokenIssuedAt` only advances when a refresh
      // response actually carries a user, so without this a response that
      // succeeds without re-seeding the store would leave the token looking
      // stale forever and the scheduler hammering every tick.
      if (Date.now() - lastAttemptRef.current < MIN_ATTEMPT_SPACING_MS) return;

      lastAttemptRef.current = Date.now();
      inFlightRef.current = true;
      void refreshAccessToken({ waitForReauth: false }).finally(() => {
        inFlightRef.current = false;
      });
    };

    const interval = setInterval(maybeRefresh, TICK_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') maybeRefresh();
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', maybeRefresh);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', maybeRefresh);
    };
  }, []);
}
